'use client'
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';

const LoginModal = dynamic(() => import('../CreateBot/LoginModal'), { ssr: false });
const RegisterModal = dynamic(() => import('../CreateBot/RegisterModal'), { ssr: false });
const ReferralPopup = dynamic(() => import('../common/ReferalProgram'), { ssr: false });
const FirstRechargeVoucherPopup = dynamic(
  () => import('../common/FirstRechargeVoucherPopup'),
  { ssr: false },
);
const SuccessModal = dynamic(
  () => import('@/app/liquidity-pool-creator/components/SuccessModal'),
  { ssr: false },
);

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [modals, setModals] = useState({
    login: false,
    register: false,
    referral: false,
    firstRechargeVoucher: false,
    liquiditySuccess: false,
  });
  const [registerPrefill, setRegisterPrefill] = useState({});
  const [liquiditySuccessData, setLiquiditySuccessData] = useState(null);

  const openModal = useCallback((modalName) => {
    setModals(prev => {
      // Close referral modal when opening login or register
      if (modalName === 'login' || modalName === 'register') {
        return {
          ...prev,
          referral: false,
          firstRechargeVoucher: false,
          [modalName]: true
        };
      }
      if (modalName === 'referral') {
        return {
          ...prev,
          firstRechargeVoucher: false,
          referral: true,
        };
      }
      if (modalName === 'firstRechargeVoucher') {
        return {
          ...prev,
          login: false,
          register: false,
          referral: false,
          firstRechargeVoucher: true,
        };
      }
      return {
        ...prev,
        [modalName]: true
      };
    });
  }, []);

  const closeModal = useCallback((modalName) => {
    setModals(prev => ({
      ...prev,
      [modalName]: false
    }));
  }, []);

  const closeAllModals = useCallback(() => {
    setModals({
      login: false,
      register: false,
      referral: false,
      firstRechargeVoucher: false,
      liquiditySuccess: false,
    });
  }, []);

  const openReferralModal = useCallback(() => {
    openModal('referral');
  }, [openModal]);

  const closeReferralModal = useCallback(() => {
    closeModal('referral');
  }, [closeModal]);

  const openFirstRechargeVoucherModal = useCallback(() => {
    openModal('firstRechargeVoucher');
  }, [openModal]);

  const closeFirstRechargeVoucherModal = useCallback(() => {
    closeModal('firstRechargeVoucher');
  }, [closeModal]);

  const openLiquiditySuccess = useCallback((data) => {
    setLiquiditySuccessData(data || null);
    openModal('liquiditySuccess');
  }, [openModal]);

  const closeLiquiditySuccess = useCallback(() => {
    setLiquiditySuccessData(null);
    closeModal('liquiditySuccess');
  }, [closeModal]);

  const switchToRegister = useCallback((prefillData = {}) => {
    setRegisterPrefill((prev) => ({
      ...prev,
      ...prefillData,
    }));
    setModals({
      login: false,
      register: true,
      referral: false,
      firstRechargeVoucher: false,
    });
  }, []);

  const switchToLogin = useCallback(() => {
    setModals({
      login: true,
      register: false,
      referral: false,
      firstRechargeVoucher: false,
    });
  }, []);

  const openLoginModal = useCallback(() => openModal('login'), [openModal]);
  const openRegisterModal = useCallback(() => openModal('register'), [openModal]);

  // After login/signup, dismiss auth / first-recharge voucher modals only — keep referral & tool modals open.
  useEffect(() => {
    if (!isAuthenticated) return;
    setModals((prev) => ({
      ...prev,
      login: false,
      register: false,
      firstRechargeVoucher: false,
    }));
  }, [isAuthenticated]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    modals,
    openModal,
    openLoginModal,
    openRegisterModal,
    closeModal,
    closeAllModals,
    switchToRegister,
    switchToLogin,
    registerPrefill,
    setRegisterPrefill,
    openReferralModal,
    closeReferralModal,
    openFirstRechargeVoucherModal,
    closeFirstRechargeVoucherModal,
    /** @deprecated Use openFirstRechargeVoucherModal */
    openPromoCodeModal: openFirstRechargeVoucherModal,
    /** @deprecated Use closeFirstRechargeVoucherModal */
    closePromoCodeModal: closeFirstRechargeVoucherModal,
    openLiquiditySuccess,
    closeLiquiditySuccess,
  }), [modals, openModal, openLoginModal, openRegisterModal, closeModal, closeAllModals, switchToRegister, switchToLogin, registerPrefill, openReferralModal, closeReferralModal, openFirstRechargeVoucherModal, closeFirstRechargeVoucherModal, openLiquiditySuccess, closeLiquiditySuccess]);

  // Memoize the close handlers to prevent infinite loops
  const handleCloseLogin = useCallback(() => closeModal('login'), [closeModal]);
  const handleCloseRegister = useCallback(() => closeModal('register'), [closeModal]);
  const handleCloseReferral = useCallback(() => closeModal('referral'), [closeModal]);
  const handleCloseFirstRechargeVoucher = useCallback(
    () => closeModal('firstRechargeVoucher'),
    [closeModal],
  );

  return (
    <ModalContext.Provider value={value}>
      {children}

      {modals.login ? (
        <LoginModal
          isOpen={modals.login}
          onClose={handleCloseLogin}
          onSwitchToRegister={switchToRegister}
        />
      ) : null}

      {modals.register ? (
        <RegisterModal
          isOpen={modals.register}
          onClose={handleCloseRegister}
          onSwitchToLogin={switchToLogin}
          prefillData={registerPrefill}
        />
      ) : null}

      {modals.referral ? (
        <ReferralPopup
          isOpen={modals.referral}
          onClose={handleCloseReferral}
          isdev={false}
        />
      ) : null}

      {modals.firstRechargeVoucher ? (
        <FirstRechargeVoucherPopup
          isOpen={modals.firstRechargeVoucher}
          onClose={handleCloseFirstRechargeVoucher}
          onSignUp={() => openModal('register')}
        />
      ) : null}

      {modals.liquiditySuccess ? (
        <SuccessModal
          isOpen={modals.liquiditySuccess}
          onClose={closeLiquiditySuccess}
          poolAddress={liquiditySuccessData?.poolAddress}
          baseToken={liquiditySuccessData?.baseToken}
          quoteToken={liquiditySuccessData?.quoteToken}
        />
      ) : null}
    </ModalContext.Provider>
  );
};
